import React from "react";
import { withTranslation } from "react-i18next";
import { Button, Row, Col, Form, Input, FormFeedback } from "reactstrap";
import { useFormik } from "formik";
import { useToasts } from "utils/useHotToast";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import { ThailandAddressTypeahead, ThailandAddressValue } from "react-thailand-address-typeahead";
import { AddressValidationSchema } from "model/_base/address";

/**
 * AddressForm - A reusable form component for user information
 * This component only handles form state and validation.
 * All submission logic is handled by the parent component.
 *
 * @param {Object} data - Initial form data
 * @param {Function} onSwitchView - Callback when switching between view/edit modes
 * @param {boolean} isEditMode - Whether the form is in edit mode
 * @param {Object} formRef - Ref to access form methods from parent
 * @param {Function} onSubmit - Callback when form is submitted with values
 * @param {boolean} isSubmitting - Whether the form is in submitting state
 * @param {Object} submitError - Error object from submission
 */
const AddressForm = ({ data, onSwitchView, isEditMode = true, formRef, onSubmit, isSubmitting = false }) => {
  // Expose validation methods to parent
  React.useImperativeHandle(formRef, () => ({
    submitForm: validation.submitForm,
    values: validation.values,
    isValid: validation.isValid,
    errors: validation.errors,
    touched: validation.touched,
    setFieldValue: validation.setFieldValue,
    setFieldTouched: validation.setFieldTouched,
    validateForm: validation.validateForm,
  }));

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: data?.addresses[0]?.id,
      addressType: "Home",
      addressLine1: data?.addresses[0]?.addressLine1,
      addressLine2: "",
      subDistrict: data?.addresses[0]?.subDistrict,
      district: data?.addresses[0]?.district,
      province: data?.addresses[0]?.province,
      postalCode: data?.addresses[0]?.postalCode,
      country: "Thailand",
      isPrimary: false,
    },
    validationSchema: AddressValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (isEditMode && onSubmit) {
        try {
          // Map camelCase to snake_case for the API
          const mappedValues = {
            id: values.id,
            address_type: values.addressType,
            address_line1: values.addressLine1,
            address_line2: values.addressLine2,
            sub_district: values.subDistrict,
            district: values.district,
            province: values.province,
            postal_code: values.postalCode,
            country: values.country,
            is_primary: values.isPrimary,
          };
          await onSubmit(mappedValues);
        } finally {
          setSubmitting(false);
        }
      }
    },
  });

  const handleAddressSelect = (addressData) => {
    validation.setValues({
      ...validation.values,
      subDistrict: addressData.subdistrict,
      district: addressData.district,
      province: addressData.province,
      postalCode: addressData.postalCode,
    });
  };

  const handleCancel = () => {
    if (isEditMode && onSwitchView) {
      onSwitchView();
    }
  };

  return (
    <Form onSubmit={validation.handleSubmit} id={!isEditMode ? "address-form" : undefined}>
      <div className="d-flex flex-column mt-3 gap-4">
        <Row>
          <Col xs={12}>
            <div className="text-2xl whitespace-nowrap">ที่อยู่ผู้เป็นสมาชิก</div>
            <Input
              //
              name="addressLine1"
              type="textarea"
              placeholder="บ้านเลขที่ / หมู่บ้าน / ซอย / ถนน"
              value={validation.values.addressLine1 || ""}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              disabled={validation.isSubmitting}
              invalid={validation.touched.addressLine1 && validation.errors.addressLine1}
              data-testid="addressLine-input"
            />
            {validation.touched.addressLine1 && validation.errors.addressLine1 && <FormFeedback>{validation.errors.addressLine1}</FormFeedback>}
          </Col>
        </Row>

        <ThailandAddressTypeahead
          value={ThailandAddressValue.fromDatasourceItem({
            d: validation.values.district,
            p: validation.values.province,
            po: validation.values.postalCode,
            s: validation.values.subDistrict,
          })}
          onValueChange={(value) => {
            console.log(value);
            validation.setValues({
              ...validation.values,
              subDistrict: value.subdistrict,
              district: value.district,
              province: value.province,
              postalCode: value.postalCode,
            });
            handleAddressSelect(value);
          }}
        >
          <Row className="mb-3">
            <Col xs={6}>
              <div className="text-2xl whitespace-nowrap">ตำบล</div>
              <ThailandAddressTypeahead.SubdistrictInput placeholder="เลือกตำบล" className={`form-control thai-address-typeahead ${validation.touched.subDistrict && validation.errors.subDistrict ? "is-invalid" : ""}`} />
              {validation.touched.subDistrict && validation.errors.subDistrict && <FormFeedback>{validation.errors.subDistrict}</FormFeedback>}
            </Col>
            <Col xs={6}>
              <div className="text-2xl whitespace-nowrap">อำเภอ</div>
              <ThailandAddressTypeahead.DistrictInput placeholder="เลือกอำเภอ" className={`form-control thai-address-typeahead ${validation.touched.district && validation.errors.district ? "is-invalid" : ""}`} />
              {validation.touched.district && validation.errors.district && <FormFeedback>{validation.errors.district}</FormFeedback>}
            </Col>
          </Row>

          <Row>
            <Col xs={6}>
              <div className="text-2xl whitespace-nowrap">จังหวัด</div>
              <ThailandAddressTypeahead.ProvinceInput placeholder="เลือกจังหวัด" className={`form-control thai-address-typeahead ${validation.touched.province && validation.errors.province ? "is-invalid" : ""}`} />
              {validation.touched.province && validation.errors.province && <FormFeedback>{validation.errors.province}</FormFeedback>}
            </Col>
            <Col xs={6}>
              <div className="text-2xl whitespace-nowrap">รหัสไปรษณีย์</div>
              <ThailandAddressTypeahead.PostalCodeInput placeholder="รหัสไปรษณีย์" className={`form-control thai-address-typeahead ${validation.touched.postalCode && validation.errors.postalCode ? "is-invalid" : ""}`} />
              {validation.touched.postalCode && validation.errors.postalCode && <FormFeedback>{validation.errors.postalCode}</FormFeedback>}
            </Col>
          </Row>

          <ThailandAddressTypeahead.Suggestion />
        </ThailandAddressTypeahead>
      </div>
      <div className="text-end mt-4">
        <div className="d-flex justify-content-between gap-3 w-100">
          <Button color="light" outline className="w-52" onClick={handleCancel}>
            ยกเลิก
          </Button>
          <Button className="w-52" color="primary" type="submit" disabled={validation.isSubmitting || isSubmitting}>
            {isSubmitting ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default withTranslation()(AddressForm);
